import { Component } from '@theme/component';

/** Native chapter links and media work before the two tab sets are enhanced.
 * @extends {Component}
 */
class BroxCoachReference extends Component {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    super.disconnectedCallback();
  }

  #initialize() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.setAttribute('data-enhanced', '');
    this.#initializeTabs(this.querySelector('.brox-cr__navigation'), true);
    this.#initializeTabs(this.querySelector('[data-programme-tabs]'), false);
    for (const button of this.querySelectorAll('[data-reviews-toggle]')) button.removeAttribute('hidden');
    window.addEventListener('hashchange', this.#followHash, { signal: this.#controller.signal });
  }

  #hashTarget() {
    if (!location.hash) return null;
    try {
      return this.querySelector(`#${CSS.escape(decodeURIComponent(location.hash.slice(1)))}`);
    } catch {
      return null;
    }
  }

  /** @param {HTMLAnchorElement} link */
  #panel(link) {
    return link.hash ? this.querySelector(`#${CSS.escape(link.hash.slice(1))}`) : null;
  }

  /** @param {Element | null} navigation @param {boolean} profile */
  #initializeTabs(navigation, profile) {
    if (!navigation) return;
    const links = [...navigation.querySelectorAll('a')];
    if (!links.length) return;
    navigation.setAttribute('role', 'tablist');
    const hashChapter = profile ? this.#hashTarget()?.closest('[data-profile-chapter]') : null;
    const current = links.find((link) => link.hash === `#${hashChapter?.id}`)
      ?? links.find((link) => link.getAttribute('aria-selected') === 'true') ?? links[0];
    for (const link of links) {
      const panel = this.#panel(link);
      if (!panel) continue;
      if (!link.id) link.id = `${panel.id}-tab`;
      link.setAttribute('role', 'tab');
      link.setAttribute('aria-controls', panel.id);
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', link.id);
      panel.setAttribute('tabindex', '0');
      if (!profile) panel.querySelector('[data-programme-group-heading]')?.setAttribute('hidden', '');
    }
    if (current instanceof HTMLAnchorElement) this.#activateTab(current, profile);
    if (profile) this.setAttribute('data-profile-enhanced', '');
  }

  /** @param {HTMLAnchorElement} selected @param {boolean} profile */
  #activateTab(selected, profile) {
    const navigation = selected.closest(profile ? '.brox-cr__navigation' : '[data-programme-tabs]');
    if (!navigation) return;
    const selectedPanel = this.#panel(selected);
    for (const link of navigation.querySelectorAll('a')) {
      const active = link === selected;
      link.setAttribute('aria-selected', String(active));
      link.removeAttribute('aria-current');
      link.tabIndex = active ? 0 : -1;
      const panel = this.#panel(link);
      if (!(panel instanceof HTMLElement)) continue;
      panel.hidden = !active;
      if (!active) for (const video of panel.querySelectorAll('video')) video.pause();
      if (active && !profile) {
        const cta = this.querySelector('[data-programme-cta]');
        if (cta instanceof HTMLAnchorElement && panel.dataset.programmeTarget) cta.href = panel.dataset.programmeTarget;
      }
    }
    if (profile) for (const wrapper of this.querySelectorAll('.brox-cr__layout > .brox-cr__content')) {
      if (wrapper instanceof HTMLElement) wrapper.hidden = !selectedPanel || !wrapper.contains(selectedPanel);
    }
  }

  #followHash = () => {
    const target = this.#hashTarget();
    const chapter = target?.closest('[data-profile-chapter]');
    if (!(target instanceof HTMLElement) || !chapter) return;
    const tab = [...this.querySelectorAll('.brox-cr__navigation a')].find((link) => link.hash === `#${chapter.id}`);
    if (!(tab instanceof HTMLAnchorElement)) return;
    this.#activateTab(tab, true);
    target.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  };

  /** @param {MouseEvent} event @param {boolean} profile */
  #selectTab(event, profile) {
    if (!(event.target instanceof Element)) return;
    const tab = event.target.closest('[role="tab"]');
    if (!(tab instanceof HTMLAnchorElement) || !this.contains(tab)) return;
    event.preventDefault();
    this.#activateTab(tab, profile);
    if (profile) history.replaceState(null, '', tab.hash);
  }

  /** @param {KeyboardEvent} event @param {boolean} profile */
  #navigateTabs(event, profile) {
    if (!(event.target instanceof HTMLAnchorElement)) return;
    const navigation = event.target.closest('[role="tablist"]');
    if (!navigation || !this.contains(navigation)) return;
    const tabs = [...navigation.querySelectorAll('a')];
    const index = tabs.indexOf(event.target);
    let next;
    if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      const rtl = getComputedStyle(navigation).direction === 'rtl' ? -1 : 1;
      next = (index + (event.key === 'ArrowRight' ? 1 : -1) * rtl + tabs.length) % tabs.length;
    } else return;
    event.preventDefault();
    const selected = tabs[next];
    if (!(selected instanceof HTMLAnchorElement)) return;
    this.#activateTab(selected, profile);
    if (profile) history.replaceState(null, '', selected.hash);
    selected.focus();
  }

  /** @param {MouseEvent} event */
  selectProfileTab(event) { this.#selectTab(event, true); }
  /** @param {KeyboardEvent} event */
  navigateProfileTabs(event) { this.#navigateTabs(event, true); }
  /** @param {MouseEvent} event */
  selectProgrammeGroup(event) { this.#selectTab(event, false); }
  /** @param {KeyboardEvent} event */
  navigateProgrammeGroups(event) { this.#navigateTabs(event, false); }

  /** @param {MouseEvent} event - Scroll a scoped media or photo row. */
  scrollMedia(event) {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('button[data-row]');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button) || !button.dataset.row) return;
    const row = this.querySelector(`#${CSS.escape(button.dataset.row)}`);
    if (!(row instanceof HTMLElement)) return;
    const direction = button.dataset.direction === 'previous' ? -1 : 1;
    const rtl = getComputedStyle(row).direction === 'rtl' ? -1 : 1;
    row.scrollBy({ left: direction * rtl * row.clientWidth * 0.85, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  }

  /** @param {Event} event - Pause native video when its disclosure closes. */
  toggleVideo(event) {
    if (!(event.target instanceof HTMLDetailsElement) || event.target.open || !this.contains(event.target)) return;
    for (const video of event.target.querySelectorAll('video')) video.pause();
  }

  /** @param {MouseEvent} event - Reveal published reviews within this profile. */
  showReviews(event) {
    if (!(event.target instanceof HTMLButtonElement) || !this.contains(event.target)) return;
    for (const review of this.querySelectorAll('[data-extra-review]')) review.removeAttribute('hidden');
    event.target.setAttribute('aria-expanded', 'true');
    event.target.textContent = event.target.dataset.expandedLabel ?? event.target.textContent;
    event.target.disabled = true;
  }
}

if (!customElements.get('brox-coach-reference')) customElements.define('brox-coach-reference', BroxCoachReference);
